/**
 * Client RBAC: grants from the API; resolution uses the same hierarchy as the backend.
 */

import { userGrantsSatisfy } from './permission-hierarchy'
import { expandUserGrantsForEvaluation } from './expand-evaluation-grants'

export { expandUserGrantsForEvaluation } from './expand-evaluation-grants'

const EXPAND_CACHE = new Map<string, readonly string[]>()
const EXPAND_CACHE_MAX = 64

function getExpandedGrantsForChecks(normalizedGrants: string[]): readonly string[] {
  const key = buildPermissionGrantStableKey(normalizedGrants)
  const hit = EXPAND_CACHE.get(key)
  if (hit) return hit
  const expanded = Object.freeze(expandUserGrantsForEvaluation(normalizedGrants))
  if (EXPAND_CACHE.size >= EXPAND_CACHE_MAX) {
    const oldest = EXPAND_CACHE.keys().next().value
    if (oldest !== undefined) EXPAND_CACHE.delete(oldest)
  }
  EXPAND_CACHE.set(key, expanded)
  return expanded
}

export function normalize(permission: string): string {
  return permission.trim()
}

/** Order-independent memo key for grant arrays (Redux / selectors / search). */
export function buildPermissionGrantStableKey(grants: readonly string[] | undefined | null): string {
  if (!Array.isArray(grants) || grants.length === 0) return ''
  return [...grants].sort().join('|')
}

/** Roster/HR admin screens: documented for policy tests. */
const STRICT_SCREENS = new Set<string>([normalize('branch.classmanagement.view'), normalize('branch.staffdirectory.view')])

export function isStrictRequiredPermission(permission: string): boolean {
  return STRICT_SCREENS.has(normalize(permission))
}

/**
 * Exact string match in a list (e.g. feature catalog check).
 */
export function permissionKeyInList(key: string, list: readonly string[]): boolean {
  const k = normalize(key)
  if (!k) return false
  for (const x of list) {
    if (normalize(x) === k) return true
  }
  return false
}

/**
 * True if the user's grants satisfy the required catalog key (action lattice + attendance umbrella; see `permission-hierarchy.ts`).
 */
export function hasPermission(required: string, userPermissions: readonly string[]): boolean {
  const r = normalize(required)
  if (!r) return false
  const normalized = userPermissions.map(normalize).filter(Boolean)
  const effective = getExpandedGrantsForChecks(normalized)
  return userGrantsSatisfy(effective, r)
}

/** True if any listed catalog key is satisfied (non-empty keys only). */
export function hasAnyMatchingGrant(
  keys: readonly string[],
  userPermissions: readonly string[]
): boolean {
  if (!keys.length) return false
  return keys.some((k) => hasPermission(k, userPermissions))
}
